import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { ContinueWith } from "@/components/ContinueWith";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getSignupStatus, signInErrorMessage } from "@/lib/auth";

export function Login() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // GitHub sign-in comes back here with ?error= when it was refused.
  const [error, setError] = useState(() => signInErrorMessage(params.get("error"), params.get("via")));
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  // "Create an account" only when anyone may sign up (invite-only signups
  // arrive by their own link).
  const [signupOpen, setSignupOpen] = useState(false);
  // "Continue with GitHub" whenever it is set up: existing Members sign in
  // with it even while signups are closed.
  const [providers, setProviders] = useState<string[]>([]);
  useEffect(() => {
    getSignupStatus().then((status) => {
      setSignupOpen(status.signup_open);
      setProviders(status.sign_in_providers ?? []);
    });
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="canvas-ambient relative flex h-screen items-center justify-center bg-canvas">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-sm overflow-hidden rounded-xl border border-line-subtle bg-surface p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] space-y-6"
      >
        {/* Top accent gradient — anchors brand */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric-blue/60 to-transparent"
        />

        <div className="text-center space-y-2">
          <div className="relative mx-auto h-12 w-12">
            <span
              aria-hidden="true"
              className="absolute inset-0 -m-2 rounded-full bg-electric-blue/15 blur-md"
            />
            <img
              src="/phantm-logo.png"
              alt="Phantm"
              className="relative mx-auto h-12 w-12 object-contain"
            />
          </div>
          <h1 className="pt-1 text-xl font-semibold tracking-tight text-fg">
            Phantm Dashboard
          </h1>
          <p className="text-sm text-fg-muted">
            Sign in to manage your LLM gateway
          </p>
        </div>

        {error && (
          <div role="alert" className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
            {error}
          </div>
        )}

        <ContinueWith providers={providers} origin="login" />

        <div className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="text-[10px] font-medium uppercase tracking-[0.14em] text-fg-faint"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoFocus
              autoComplete="email"
              className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="text-[10px] font-medium uppercase tracking-[0.14em] text-fg-faint"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={!email.trim() || !password || submitting}
          className="w-full rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white shadow-[0_8px_24px_-12px_rgba(37,99,235,0.55)] transition-all hover:bg-electric-blue/90 hover:shadow-[0_10px_28px_-10px_rgba(37,99,235,0.7)] focus:outline-none focus:ring-2 focus:ring-electric-blue/50 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
        >
          {submitting ? "Signing in…" : "Sign In"}
        </button>

        <p className="text-center">
          <Link
            to="/forgot-password"
            className="text-xs text-fg-muted transition-colors hover:text-electric-blue"
          >
            Forgot password?
          </Link>
        </p>

        {signupOpen && (
          <p className="text-center text-xs text-fg-muted">
            New to Phantm?{" "}
            <Link to="/signup" className="text-electric-blue hover:underline">
              Create an account
            </Link>
          </p>
        )}

        <p className="pt-2 text-center text-[11px] text-fg-faint">
          Drop-in, OpenAI-compatible. Optimized for every request.
        </p>
      </form>
    </div>
  );
}
